import { useTranslation } from 'react-i18next';
import type { LinkProps } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { Button } from '~/components/Button';
import { cn } from '~/lib/cn';

import type { InternalLinkType } from '../../types';
import { BASE_LINK_ITEM_CLASSES } from './constants';

export interface InternalLinkItemProps {
  isNew: boolean;
  previewImgSrc: string;
  url: string;
  actionTextKey: string;
  descriptionKey: string;
  className?: string;
  onClick?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    type?: InternalLinkType,
  ) => void;
  type?: InternalLinkType;
  linkProps?: Omit<LinkProps, 'to'>;
}

export function InternalLinkItem({
  isNew,
  previewImgSrc,
  url,
  actionTextKey,
  descriptionKey,
  className = '',
  onClick,
  type,
  linkProps = {},
}: InternalLinkItemProps) {
  const { t } = useTranslation('wmgone');

  return (
    <Link {...linkProps} to={url} onClick={(event) => onClick?.(event, type)}>
      <div
        className={cn(
          BASE_LINK_ITEM_CLASSES,
          'h-full w-[478px] py-[15px] pl-lg pr-md',
          className,
        )}
      >
        <div className="flex items-center gap-[20px]">
          <div className="flex h-full flex-1 flex-col justify-center py-sm">
            {isNew ? (
              <div
                className={cn(
                  'mb-auto flex h-[26px] w-[46px] items-center justify-center',
                  'rounded-xs text-body-sm bg-default-inverse text-strong-on-light',
                )}
              >
                {t('links.new')}
              </div>
            ) : null}

            <div className="flex flex-col gap-sm">
              <span className="text-body-md text-strong-on-dark">
                {t(descriptionKey)}
              </span>

              <Button
                variant="secondary"
                className="w-[175px] text-body-sm text-strong hover:bg-transparent hover:outline-default"
                asChild
              >
                <span className="text-center">{t(actionTextKey)}</span>
              </Button>
            </div>
          </div>
          {/* Fixed height for the images.
              See here: https://www.figma.com/design/xiWIR4OXM716yPIMWEiP40?node-id=4369-527#1399150468 */}
          <div className="h-[152px] min-w-[228px] flex-1">
            <img
              src={previewImgSrc}
              alt=""
              className="h-full justify-self-center"
            />
          </div>
        </div>
      </div>
    </Link>
  );
}

InternalLinkItem.displayName = 'InternalLinkItem';
